/*
 * pic-nn-web-ui
 *
 * Web UI for neural network applications.
 *
 * @copyright 2020 VSTE CB <http://www.vstecb.cz/>.
 * @license See LICENSE file distributed with this source code for more information.
 */

import { IModelSpec } from "./IModelSpec";
import { ModelHandler } from "./model/ModelHandler";
import { IModelExportData } from "./IModelExportData";
import { ModelInput, IModelInputBaseConfig } from "./model/ModelInput";
import { ModelOutput } from "./model/ModelOutput";
import { formatNumericValue } from "./helpers";

export class InvalidModelDataError extends Error {}

export class InvalidModelVersionError extends Error {

	public constructor(
		public currentSpec: IModelSpec,
		public data: IModelExportData
	) {

		super(`Model version are incompatible. Model: '${currentSpec.title} / ${currentSpec.version}', Data: '${data.title} / ${data.version}'.`);

	}

}

/**
 * Exports model to data object
 *
 * @param spec Model spec
 * @param handler Model handler
 */
export function modelExport(spec: IModelSpec, handler: ModelHandler): IModelExportData {

	const data: IModelExportData = {
		id: spec.id,
		title: spec.title,
		version: spec.version,
		data: handler.getData()
	};

	return data;

}

/**
 * Imports data object into a model
 *
 * @param spec Model spec
 * @param handler Model handler
 * @param data Imported data
 */
export function modelImport(spec: IModelSpec, handler: ModelHandler, data: IModelExportData) {

	// Validate
	if (!(data instanceof Object)) {
		throw new InvalidModelDataError("Model data must be an object.");
	}

	if (!data.id || !data.title || !data.version || !data.data) {
		throw new InvalidModelDataError("Model data must contain the following properties: 'id', 'title', 'version' and 'data'.");
	}

	if (!(data.data instanceof Object)) {
		throw new InvalidModelDataError("Model data property 'data' must be an object.");
	}

	if (!(data.data.inputs instanceof Object) || !(data.data.outputs instanceof Object)) {
		throw new InvalidModelDataError("Model data property 'data' must contain properties 'inputs' and 'outputs' which must be objects.");
	}

	// Check model ID
	if (data.id !== spec.id) {
		throw new InvalidModelVersionError(spec, data);
	}

	// Set data
	handler.setData(data.data);

}

/**
 * Exports model to CSV file
 *
 * @param spec Model spec
 * @param handler Model handler
 */
export function modelExportCSV(spec: IModelSpec, handler: ModelHandler): string {

	const lines = [
		`ID modelu;${spec.id};;`,
		`Název modelu;${spec.title};;`,
		`Verze modelu;${spec.version};;`,
		`;;;`
	];

	lines.push(`Vstupy;;;`);

	for (const k in handler.inputs) {

		const state = (handler.inputs[k] as ModelInput<any, any, IModelInputBaseConfig>).getState();
		const val = formatNumericValue(state.value, null, null, ",");

		lines.push(
			`${state.config.label};${val};${state.config.description || ""}`
		);

	}

	lines.push(`;;;`);
	lines.push(`Výstupy;;;`);

	for (const k in handler.outputs) {

		const state = (handler.outputs[k] as ModelOutput<any>).getState();
		const val = formatNumericValue(state.value, state.config.decimals, state.config.multiplier, ",");

		const interpretation = [];

		if (state.config.resultLabels) {
			for (let i = 0; i < state.config.resultLabels.length; i++) {

				const resultLabel = state.config.resultLabels[i];

				if (resultLabel.condition(state, handler)) {
					interpretation.push(`${resultLabel.prefix}: ${resultLabel.content}`);
				}

			}
		}

		lines.push(`${state.config.label};${val}${state.config.suffix || ""};${state.config.description || ""};${interpretation.join(".")}`);
	}

	return lines.join("\n");

}
